import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTranslation } from "react-i18next";
import InnerProgressSpinner from './progressSpinner/innerProgressSpinner';
import { encryptPassword } from '../RsaEncryptor';

const LoginModal = ({ onClose }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { login, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  const handleLogin = async () => {
    setError("");
    if (!username.trim() || !password.trim()) {
      setError(t('please_enter_username_and_password'));
      return;
    }

    const encryptedPassword = encryptPassword(password);
    const result = await login(username, encryptedPassword, i18n);
    if (!result.success) {
      setError(result.message || t('login_failed'));
    } else {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div
      dir={t('dir')}
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4"
      onClick={handleClickOutside}
    >
      {loading && <InnerProgressSpinner loading={loading} />}
      <div
        ref={modalRef}
        className="bg-white w-full max-w-md p-6 sm:p-8 rounded-lg shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-4 text-red-600 text-2xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          ✖
        </button>

        <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center">{t('login')}</h2>

        {error && <p className="text-red-600 mb-3 text-sm text-center">{error}</p>}

        <input
          type="text"
          placeholder={t('username')}
          className="w-full mb-3 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none uppercase"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />

        <input
          type="password"
          placeholder={t('password')}
          className="w-full mb-4 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded transition duration-200 focus:ring-2 focus:ring-blue-500 outline-none"
        >
          {loading ? t('logging') : t('login')}
        </button>
      </div>
    </div>
  );
};

export default LoginModal;
